import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { NeonText } from '../effects';
import { useLanguage } from '@/i18n';
import { TECH_URLS } from '@/constants';
import { FiChevronDown } from 'react-icons/fi';
import styles from './About.module.css';
import {
  SiUnity,
  SiReact,
  SiNextdotjs,
  SiTypescript,
  SiJavascript,
  SiSupabase,
  SiNetlify,
  SiAutodesk,
  SiGit,
  SiRider,
  SiPhpstorm,
  SiPython,
  SiOpenai,
  SiMeta,
  SiHtc,
  SiGooglecloud,
  SiDocker,
} from 'react-icons/si';
import { FaJava, FaWindows } from 'react-icons/fa';
import { VscVscodeInsiders } from 'react-icons/vsc';
import { MdVrpano } from 'react-icons/md';
import { RiRobot2Fill } from 'react-icons/ri';
import {
  CSharpIcon,
  VRayIcon,
  ClaudeIcon,
  LangChainIcon,
  PicoIcon,
  CursorIcon,
  AzureIcon,
  AWSIcon,
} from '../icons/TechIcons';
import aboutImage from '@/assets/fernando-about.jpg';

interface AccordionCardProps {
  icon: string;
  title: string;
  children: React.ReactNode;
}

function AccordionCard({ icon, title, children }: AccordionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 900);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isMobile) {
    return (
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>
          <span className={styles.cardIcon}>{icon}</span>
          {title}
        </h3>
        <div className={styles.cardContent}>{children}</div>
      </div>
    );
  }

  return (
    <div className={`${styles.card} ${styles.cardAccordion}`}>
      <button
        className={styles.cardHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <h3 className={styles.cardTitle}>
          <span className={styles.cardIcon}>{icon}</span>
          {title}
        </h3>
        <FiChevronDown className={`${styles.cardToggle} ${isExpanded ? styles.cardToggleOpen : ''}`} />
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className={styles.cardContent}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  const TechItem = ({ icon: Icon, name }: { icon: React.ComponentType<{ className?: string }>; name: string }) => {
    const url = TECH_URLS[name];
    const content = (
      <>
        <Icon className={styles.techIcon} />
        <span className={styles.techName}>{name}</span>
      </>
    );

    if (url) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.techItem}
          aria-label={`Visit ${name} official site`}
        >
          {content}
        </a>
      );
    }

    return <div className={styles.techItem}>{content}</div>;
  };

  return (
    <section id="about-me" className={styles.about} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <NeonText as="h2" color="pink" effect="glow" className={styles.title} animate={false}>
            {t.about.greeting}
          </NeonText>
        </motion.div>

        <div className={styles.content}>
          {/* Photo on the left */}
          <motion.div
            className={styles.photoSection}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className={styles.photoFrame}>
              <img src={aboutImage} alt="Fernando Olea" className={styles.aboutPhoto} />
              <div className={styles.photoOverlay} />
            </div>
            <div className={styles.photoCaption}>
              <span className={styles.name}>Fernando Olea</span>
              <span className={styles.role}>
                {t.about.role.split('\n').map((line, i, arr) => (
                  <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </span>
            </div>
            <div className={styles.introText}>
              <p className={styles.highlight} dangerouslySetInnerHTML={{ __html: t.about.intro.highlight }} />
              <p className={styles.specialization} dangerouslySetInnerHTML={{ __html: t.about.intro.specialization }} />
            </div>
          </motion.div>

          {/* Info cards on the right */}
          <motion.div
            className={styles.description}
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <AccordionCard icon="🚀" title={t.about.cards.leadership.title}>
              <p dangerouslySetInnerHTML={{ __html: t.about.cards.leadership.content }} />
            </AccordionCard>

            <AccordionCard icon="💼" title={t.about.cards.experience.title}>
              <p dangerouslySetInnerHTML={{ __html: t.about.cards.experience.content }} />
            </AccordionCard>

            <AccordionCard icon="🤖" title={t.about.cards.aiDevelopment.title}>
              <p dangerouslySetInnerHTML={{ __html: t.about.cards.aiDevelopment.content }} />
            </AccordionCard>
          </motion.div>

          {/* Tech Stack Section */}
          <motion.div
            className={styles.techStack}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3 className={styles.techStackTitle}>{t.about.techStack}</h3>

            {/* Core & XR - Traditional + AI */}
            <div className={styles.techCategory}>
              <h4 className={styles.categoryTitle}>{t.about.techCategories.unityXr} <span className={styles.categoryBadge}>{t.about.techCategories.unityXrBadge}</span></h4>
              <div className={styles.techGrid}>
                <TechItem icon={SiUnity} name="Unity3D" />
                <TechItem icon={MdVrpano} name="AR/VR/MR" />
                <TechItem icon={MdVrpano} name="WebXR" />
                <TechItem icon={MdVrpano} name="Digital Twins" />
                <TechItem icon={FaWindows} name="HoloLens 2" />
                <TechItem icon={SiMeta} name="Meta Quest" />
                <TechItem icon={PicoIcon} name="Pico" />
                <TechItem icon={SiHtc} name="HTC Vive" />
              </div>
            </div>

            {/* Full Stack Web & Code - AI-Powered */}
            <div className={styles.techCategory}>
              <h4 className={styles.categoryTitle}>{t.about.techCategories.fullStackWeb} <span className={styles.categoryBadge}>{t.about.techCategories.fullStackBadge}</span></h4>
              <div className={styles.techGrid}>
                <TechItem icon={SiReact} name="React" />
                <TechItem icon={SiNextdotjs} name="Next.js" />
                <TechItem icon={SiTypescript} name="TypeScript" />
                <TechItem icon={SiJavascript} name="JavaScript" />
                <TechItem icon={SiSupabase} name="Supabase" />
                <TechItem icon={SiNetlify} name="Netlify" />
                <TechItem icon={SiDocker} name="Docker" />
                <TechItem icon={FaJava} name="Java" />
                <TechItem icon={CSharpIcon} name="C#" />
                <TechItem icon={SiPython} name="Python" />
              </div>
            </div>

            {/* 3D & Rendering */}
            <div className={styles.techCategory}>
              <h4 className={styles.categoryTitle}>{t.about.techCategories.rendering3d}</h4>
              <div className={styles.techGrid}>
                <TechItem icon={SiUnity} name="Unity WebGL" />
                <TechItem icon={SiAutodesk} name="3ds Max" />
                <TechItem icon={VRayIcon} name="V-Ray" />
              </div>
            </div>

            {/* AI Tools */}
            <div className={styles.techCategory}>
              <h4 className={styles.categoryTitle}>{t.about.techCategories.aiTools}</h4>
              <div className={styles.techGrid}>
                <TechItem icon={ClaudeIcon} name="Claude Code" />
                <TechItem icon={CursorIcon} name="Cursor AI" />
                <TechItem icon={SiOpenai} name="ChatGPT" />
                <TechItem icon={LangChainIcon} name="LangChain/LangGraph" />
                <TechItem icon={RiRobot2Fill} name="RAG" />
                <TechItem icon={RiRobot2Fill} name="Embeddings" />
                <TechItem icon={RiRobot2Fill} name="Vector DB" />
                <TechItem icon={RiRobot2Fill} name="Fine-tuning" />
                <TechItem icon={RiRobot2Fill} name="AI Agents" />
              </div>
            </div>

            {/* Development Tools */}
            <div className={styles.techCategory}>
              <h4 className={styles.categoryTitle}>{t.about.techCategories.devTools}</h4>
              <div className={styles.techGrid}>
                <TechItem icon={SiGit} name="Git" />
                <TechItem icon={SiRider} name="Rider" />
                <TechItem icon={VscVscodeInsiders} name="VSCode" />
                <TechItem icon={SiPhpstorm} name="PhpStorm" />
                <TechItem icon={CursorIcon} name="Cursor" />
              </div>
            </div>

            {/* DevOps & Cloud */}
            <div className={styles.techCategory}>
              <h4 className={styles.categoryTitle}>{t.about.techCategories.devOps}</h4>
              <div className={styles.techGrid}>
                <TechItem icon={AWSIcon} name="AWS" />
                <TechItem icon={SiGooglecloud} name="Google Cloud" />
                <TechItem icon={AzureIcon} name="Azure" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
